// My Workspace — Phase 1: free-form notes.
//
// A calm personal space: edit notes in a WYSIWYG editor (Toast UI — bold shows
// as bold while you type) that stores plain Markdown underneath, read them back
// rendered via the app's own renderer, and export any note as a portable .md
// file or a Word-openable document. Logged-in only; sharing and files later.
import { isAuthed } from "../lib/auth.js";
import { openAuthPanel } from "../ui/authPanel.js";
import { escapeHtml } from "../ui/helpers.js";
import { renderMarkdown } from "../ui/markdown.js";
import { fetchNotes, createNote, updateNote, deleteNote } from "../lib/notes.js";
import { fetchVerbs, fetchArticles, fetchWorkspace, overlay } from "../lib/data.js";
import { navigate } from "../router/router.js";
import { uploadUserImage } from "../cms/api.js";
import { GUIDES } from "./quickGuides.js";

export async function workspaceView(app) {
  if (!isAuthed()) {
    app.innerHTML = `
      <section class="max-w-xl mx-auto py-12 text-center">
        <p class="text-4xl mb-3">🗂️</p>
        <h2 class="text-2xl font-bold text-sage-800">My Workspace</h2>
        <p class="text-sage-600 text-sm mt-2 mb-5">Your private space for study notes — written in Markdown, rendered nicely, and exportable. Sign in to start.</p>
        <button id="ws-signin" class="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2.5 rounded-lg text-sm font-medium">Sign in</button>
      </section>`;
    app.querySelector("#ws-signin").addEventListener("click", () => openAuthPanel("signin"));
    return;
  }
  await renderList(app);
  window.scrollTo({ top: 0, behavior: "smooth" });
}

function snippet(md) {
  return (md || "")
    .replace(/[#>*`_\-|]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 110);
}

function fmtDate(s) {
  try {
    return new Date(s).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

// The "Pinned" section — verbs & articles the user has pinned (📌). Brought over
// from the old dashboard workspace so the personal hub lives in one place.
function pinnedSectionHTML(verbs, articles, guides) {
  const pv = verbs.filter((v) => v.is_pinned);
  const pa = articles.filter((a) => a.is_pinned);
  const pg = guides || [];
  if (!pv.length && !pa.length && !pg.length) {
    return `
      <div class="bg-white border border-dashed border-sage-200 rounded-2xl p-5 text-center">
        <p class="text-sage-600 text-sm">Nothing pinned yet.</p>
        <p class="text-xs text-sage-400 mt-1">Pin verbs, articles, and quick guides (📌) as you study — they gather here, synced across devices.</p>
      </div>`;
  }
  const verbChips = pv
    .map((v) => `<button data-verb="${escapeHtml(v.infinitive)}" class="text-sm px-3 py-1.5 rounded-full bg-sage-50 border border-sage-200 text-sage-700 hover:border-sage-400">${v.emoji ? v.emoji + " " : ""}${escapeHtml(v.infinitive)}</button>`)
    .join("");
  const artChips = pa
    .map((a) => `<button data-article="${escapeHtml(a.slug)}" class="text-sm px-3 py-1.5 rounded-full bg-white border border-sage-200 text-sage-700 hover:border-sage-400">${escapeHtml(a.title)}</button>`)
    .join("");
  const guideChips = pg
    .map((g) => `<button data-guide="${escapeHtml(g.nav)}" class="text-sm px-3 py-1.5 rounded-full bg-white border border-sage-200 text-sage-700 hover:border-sage-400">${g.emoji} ${escapeHtml(g.title)}</button>`)
    .join("");
  const block = (label, chips, more) =>
    `<p class="text-xs font-semibold text-sage-500 uppercase tracking-wide mb-2">${label}</p><div class="flex flex-wrap gap-2 ${more ? "mb-4" : ""}">${chips}</div>`;
  return `
    <div class="bg-white border border-sage-100 rounded-2xl p-5">
      ${pv.length ? block("Pinned verbs", verbChips, pa.length || pg.length) : ""}
      ${pa.length ? block("Pinned articles", artChips, pg.length) : ""}
      ${pg.length ? block("Pinned guides", guideChips, false) : ""}
    </div>`;
}

async function renderList(app) {
  app.innerHTML = `<div class="py-16 text-center text-sage-400 text-sm">Loading your workspace…</div>`;
  const [notes, verbs, articles, workspace] = await Promise.all([
    fetchNotes(),
    fetchVerbs(),
    fetchArticles(),
    fetchWorkspace(),
  ]);
  const oVerbs = overlay(verbs, workspace, "verbs");
  const oArticles = overlay(articles, workspace, "grammar_articles");
  const guideBySlug = Object.fromEntries(GUIDES.map((g) => [g.nav, g]));
  const pinnedGuides = workspace
    .filter((w) => w.collection_type === "guide" && w.is_pinned)
    .map((w) => guideBySlug[w.item_id])
    .filter(Boolean);
  const totalPins = oVerbs.filter((v) => v.is_pinned).length + oArticles.filter((a) => a.is_pinned).length + pinnedGuides.length;

  const cards = notes.length
    ? notes
        .map(
          (n) => `
        <button data-note="${n.id}" class="w-full text-left bg-white border border-sage-100 rounded-2xl p-4 hover:border-sage-300 transition">
          <div class="flex items-start justify-between gap-3">
            <h3 class="font-semibold text-sage-800 truncate">${escapeHtml(n.title || "Untitled note")}</h3>
            <span class="shrink-0 text-[11px] text-sage-400">${fmtDate(n.updated)}</span>
          </div>
          <p class="text-sm text-sage-500 mt-1 line-clamp-2">${escapeHtml(snippet(n.body_md)) || "<span class='italic'>Empty</span>"}</p>
        </button>`
        )
        .join("")
    : `<div class="bg-white border border-dashed border-sage-200 rounded-2xl p-8 text-center">
         <div class="text-4xl mb-3">📝</div>
         <p class="text-sage-700 font-medium">No notes yet</p>
         <p class="text-sage-500 text-sm mt-1">Jot down anything — grammar you keep forgetting, vocab lists, a sentence you liked. Markdown supported.</p>
       </div>`;

  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <div class="mb-6">
        <h2 class="text-2xl font-bold text-sage-800">My Workspace</h2>
        <p class="text-sage-600 text-sm mt-1">Your pinned references and private notes, all in one place.</p>
      </div>

      <details class="mb-8 group" ${totalPins <= 4 ? "open" : ""}>
        <summary class="cursor-pointer list-none flex items-center gap-2 mb-2 select-none">
          <span class="text-sm font-semibold text-sage-500 uppercase tracking-wide">📌 Pinned</span>
          <span class="text-xs text-sage-400">(${totalPins})</span>
          <span class="ml-auto text-sage-400 text-xs transition-transform group-open:rotate-90">▸</span>
        </summary>
        ${pinnedSectionHTML(oVerbs, oArticles, pinnedGuides)}
      </details>

      <div>
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold text-sage-500 uppercase tracking-wide">My Notes</h3>
          <button id="ws-new" class="shrink-0 bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">New note</button>
        </div>
        <div class="space-y-3">${cards}</div>
      </div>
    </section>`;

  app.querySelector("#ws-new").addEventListener("click", () => editNote(app, null));
  app.querySelectorAll("[data-note]").forEach((b) =>
    b.addEventListener("click", () => {
      const note = notes.find((n) => n.id === b.dataset.note);
      if (note) openNote(app, note);
    })
  );
  app.querySelectorAll("[data-verb]").forEach((b) =>
    b.addEventListener("click", () => navigate("verb-detail", { inf: b.dataset.verb }))
  );
  app.querySelectorAll("[data-article]").forEach((b) =>
    b.addEventListener("click", () => navigate("article", { slug: b.dataset.article }))
  );
  app.querySelectorAll("[data-guide]").forEach((b) =>
    b.addEventListener("click", () => navigate(b.dataset.guide))
  );
}

function openNote(app, note) {
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <button id="ws-back" class="text-sm font-medium text-sage-600 hover:text-sage-800 mb-4 inline-flex items-center gap-1">← All notes</button>
      <div class="bg-white border border-sage-100 rounded-2xl p-5 sm:p-7">
        <div class="flex items-start justify-between gap-3 mb-4 pb-4 border-b border-sage-100">
          <h1 class="text-2xl font-bold text-sage-800">${escapeHtml(note.title || "Untitled note")}</h1>
          <span class="shrink-0 text-xs text-sage-400 mt-1">Updated ${fmtDate(note.updated)}</span>
        </div>
        <div class="prose-dourado" data-body></div>
        <div class="mt-6 pt-4 border-t border-sage-100 flex flex-wrap gap-2">
          <button id="ws-edit" class="bg-sage-500 hover:bg-sage-600 text-white px-4 py-2 rounded-lg text-sm font-medium">Edit</button>
          <button id="ws-export" class="bg-white text-sage-600 border border-sage-200 hover:border-sage-300 px-4 py-2 rounded-lg text-sm font-medium">Export .md</button>
          <button id="ws-export-word" class="bg-white text-sage-600 border border-sage-200 hover:border-sage-300 px-4 py-2 rounded-lg text-sm font-medium">Export Word</button>
          <button id="ws-delete" class="ml-auto text-rose-600 hover:text-rose-700 px-3 py-2 rounded-lg text-sm font-medium">Delete</button>
        </div>
      </div>
    </section>`;

  app.querySelector("[data-body]").innerHTML = note.body_md
    ? renderMarkdown(note.body_md)
    : `<p class="text-sage-400 italic">This note is empty.</p>`;

  app.querySelector("#ws-back").addEventListener("click", () => renderList(app));
  app.querySelector("#ws-edit").addEventListener("click", () => editNote(app, note));
  app.querySelector("#ws-export").addEventListener("click", () => exportNote(note));
  app.querySelector("#ws-export-word").addEventListener("click", () => exportNoteWord(note));
  app.querySelector("#ws-delete").addEventListener("click", async () => {
    if (!confirm("Delete this note? This can't be undone.")) return;
    await deleteNote(note.id);
    renderList(app);
  });
  window.scrollTo({ top: 0, behavior: "smooth" });
}

async function editNote(app, note) {
  const isNew = !note;
  app.innerHTML = `
    <section class="max-w-3xl mx-auto fade-in">
      <button id="ws-cancel" class="text-sm font-medium text-sage-600 hover:text-sage-800 mb-4 inline-flex items-center gap-1">← Cancel</button>
      <div class="bg-white border border-sage-100 rounded-2xl p-5 sm:p-6">
        <input id="ws-title" type="text" maxlength="200" placeholder="Note title"
          class="w-full text-xl font-bold text-sage-800 border-0 border-b border-sage-100 focus:border-sage-400 outline-none pb-2 mb-3 placeholder:text-sage-300"
          value="${escapeHtml(isNew ? "" : note.title || "")}" />

        <div id="ws-editor"><p class="text-sage-400 text-sm py-8 text-center">Loading editor…</p></div>

        <div class="mt-4 flex gap-2">
          <button id="ws-save" class="bg-sage-500 hover:bg-sage-600 text-white px-5 py-2 rounded-lg text-sm font-medium">Save</button>
          <button id="ws-save-cancel" class="bg-white text-sage-600 border border-sage-200 hover:border-sage-300 px-4 py-2 rounded-lg text-sm font-medium">Cancel</button>
        </div>
      </div>
    </section>`;

  const titleEl = app.querySelector("#ws-title");
  let editor = null;

  function leave(after) {
    if (editor) { try { editor.destroy(); } catch { /* ignore */ } }
    after();
  }
  async function save() {
    if (!editor) return;
    const title = titleEl.value.trim();
    const body_md = editor.getMarkdown();
    if (!title && !body_md.trim()) {
      leave(() => renderList(app));
      return;
    }
    const saveBtn = app.querySelector("#ws-save");
    saveBtn.disabled = true;
    saveBtn.textContent = "Saving…";
    try {
      if (isNew) await createNote({ title, body_md });
      else await updateNote(note.id, { title, body_md });
      leave(() => renderList(app));
    } catch {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save";
      alert("Couldn't save the note. Please try again.");
    }
  }
  const cancel = () => leave(() => (isNew ? renderList(app) : openNote(app, note)));

  app.querySelector("#ws-save").addEventListener("click", save);
  app.querySelector("#ws-cancel").addEventListener("click", cancel);
  app.querySelector("#ws-save-cancel").addEventListener("click", cancel);
  titleEl.focus();
  window.scrollTo({ top: 0, behavior: "smooth" });

  // Lazy-load Toast UI only when actually editing a note — keeps it out of the
  // main bundle so first load stays fast for everyone who isn't writing.
  const editorEl = app.querySelector("#ws-editor");
  try {
    const [{ default: Editor }] = await Promise.all([
      import("@toast-ui/editor"),
      import("@toast-ui/editor/dist/toastui-editor.css"),
    ]);
    if (!document.body.contains(editorEl)) return; // navigated away while loading
    editorEl.innerHTML = "";
    editor = new Editor({
      el: editorEl,
      height: "440px",
      initialEditType: "wysiwyg",
      previewStyle: "tab",
      initialValue: isNew ? "" : note.body_md || "",
      usageStatistics: false,
      autofocus: false,
      hooks: {
        // Upload dropped/pasted images to PocketBase and insert the URL — same as
        // the article editor. Without this, Toast inlines them as huge base64
        // blobs that bloat the note and fail to save.
        addImageBlobHook: async (blob, callback) => {
          try {
            const url = await uploadUserImage(blob, blob.name || "");
            callback(url, blob.name || "image");
          } catch (_) {
            alert("Couldn't upload that image — please try again.");
          }
        },
      },
    });
  } catch {
    editorEl.innerHTML = `<p class="text-rose-600 text-sm py-6 text-center">Couldn't load the editor — please refresh and try again.</p>`;
  }
}

function slugify(s) {
  return (s || "note").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "note";
}

function download(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Portable Markdown file — opens anywhere.
function exportNote(note) {
  const front = note.title ? `# ${note.title}\n\n` : "";
  download(
    new Blob([front + (note.body_md || "")], { type: "text/markdown;charset=utf-8" }),
    `${slugify(note.title)}.md`
  );
}

// Word-openable document: the note rendered to HTML, wrapped with Office
// namespaces. Not a true .docx, but Word opens it cleanly and keeps headings/
// bold/lists/tables — and you can "Save As → .docx" from there. No dependency.
function exportNoteWord(note) {
  const inner =
    (note.title ? `<h1>${escapeHtml(note.title)}</h1>` : "") +
    (note.body_md ? renderMarkdown(note.body_md) : "");
  const html =
    "<!DOCTYPE html><html xmlns:o='urn:schemas-microsoft-com:office:office' " +
    "xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'>" +
    "<head><meta charset='utf-8'><title>" + escapeHtml(note.title || "Note") + "</title></head><body>" +
    inner + "</body></html>";
  download(new Blob(["﻿", html], { type: "application/msword" }), `${slugify(note.title)}.doc`);
}
